import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { Teilnehmer } from '@/types'
import { api } from '@/api/client'
import { assetUrl } from '@/utils/assetUrl'
import EmptyState from '@/components/EmptyState'
import Avatar from '@/components/Avatar'
import { X, Search, ChevronRight, GraduationCap, MapPin, Calendar, ChevronDown, ChevronUp, ArrowUpDown, Clock, Lock, Unlock } from 'lucide-react'

/** Filter-Typ für gefilterte TN-Ansicht */
export interface TnFilter {
  type: 'abschluss' | 'studiengang' | 'studienort' | 'firmaZahlt'
  value?: string
  detail?: string
}

/** "12/06/2025 00:00:00" → "20.06.2024"  |  "2026-09-01" → "01.09.2026" */
function formatDate(raw: string | null | undefined): string {
  if (!raw) return '\u2013'
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  const d = m ? new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])) : new Date(raw)
  if (isNaN(d.getTime())) return raw
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Datum parsen → Date-Objekt (US-Format oder ISO) */
function parseDate(raw: string | null | undefined): Date | null {
  if (!raw || raw === '0000-00-00') return null
  const m = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  const d = m ? new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])) : new Date(raw)
  return isNaN(d.getTime()) ? null : d
}

/** Studienstart (BerechnungFS) holen, Fallback auf Anmeldung */
function getStudienstart(tn: Teilnehmer): string | null {
  return tn.BerechnungFS && tn.BerechnungFS !== '0000-00-00' ? tn.BerechnungFS : tn.Anmeldung
}

/** Datum → Semester-Label: "WS 2024/25" oder "SS 2025" */
function toSemesterLabel(raw: string | null | undefined): string {
  const d = parseDate(raw)
  if (!d) return ''
  const month = d.getMonth() + 1
  const year = d.getFullYear()
  if (month >= 3 && month <= 8) return `SS${year.toString().slice(-2)}`
  const wsYear = month >= 9 ? year : year - 1
  return `WS${wsYear.toString().slice(-2)}`
}

/** Semester-Label → Sortier-Schlüssel (WS25=202509, SS26=202603) */
function semesterSortKey(label: string): number {
  const m = label.match(/^(WS|SS)(\d{2})$/)
  if (!m) return 0
  const year = 2000 + Number(m[2])
  return m[1] === 'WS' ? year * 100 + 9 : year * 100 + 3
}

/** Liegt das Datum in der Zukunft? */
function isFutureStart(raw: string | null | undefined): boolean {
  const d = parseDate(raw)
  return d ? d.getTime() > Date.now() : false
}

function isExmatrikuliert(tn: Teilnehmer): boolean {
  if (tn.ExmaGrund && tn.ExmaGrund.trim() !== '') return true
  if (tn.Exmatrikulation) {
    const d = parseDate(tn.Exmatrikulation)
    if (d && d.getTime() < Date.now()) return true
  }
  return false
}

/** "Bachelor of Arts (B.A.)" → "B.A." */
function toDLS(sg: string | null): string {
  if (!sg) return ''
  const m = sg.match(/\(([^)]+)\)/)
  return m ? m[1] : sg
}

/** Voller Studiengang: "B.Sc. Wirtschaftsinformatik" */
function fullStudiengang(t: Teilnehmer): string {
  const kuerzel = toDLS(t.Studiengang)
  const fach = t.Studienfach ?? ''
  return [kuerzel, fach].filter(Boolean).join(' ')
}

function applyFilter(teilnehmer: Teilnehmer[], filter: TnFilter | null): Teilnehmer[] {
  if (!filter) return teilnehmer
  switch (filter.type) {
    case 'abschluss':
      if (filter.value === 'Bachelor')
        return teilnehmer.filter((t) => t.Studiengang?.toLowerCase().includes('bachelor'))
      if (filter.value === 'Master')
        return teilnehmer.filter((t) => t.Studiengang?.toLowerCase().includes('master'))
      if (filter.value === 'Lehrgang')
        return teilnehmer.filter((t) =>
          !t.Studiengang?.toLowerCase().includes('bachelor') &&
          !t.Studiengang?.toLowerCase().includes('master')
        )
      return teilnehmer
    case 'studiengang':
      return teilnehmer.filter((t) => {
        const sgMatch = t.Studiengang === filter.value
        const sfMatch = !filter.detail || t.Studienfach === filter.detail
        return sgMatch && sfMatch
      })
    case 'studienort':
      return teilnehmer.filter((t) => t.Studienort === filter.value)
    case 'firmaZahlt':
      return teilnehmer.filter((t) => t.FirmaZahlt)
    default:
      return teilnehmer
  }
}

/** Avatar mit Foto */
function TnAvatar({ tn, accountId }: { tn: Teilnehmer; accountId: string }) {
  return (
    <Avatar
      photoUrl={`/api/firma/${accountId}/teilnehmer/${tn.PersonenFID}/photo`}
      fallbackName={`${tn.Vorname ?? ''} ${tn.Nachname ?? ''}`.trim()}
      size={48}
    />
  )
}

/** Multi-Select Dropdown-Filter */
function FilterButton({ label, options, value, onChange }: {
  label: string
  options: string[]
  value: string[]
  onChange: (v: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const count = value.length
  const display = count === 0 ? label : count === 1 ? value[0] : `${label} (${count})`

  const toggle = (opt: string) => {
    if (value.includes(opt)) onChange(value.filter(v => v !== opt))
    else onChange([...value, opt])
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm border-2 transition-all
          ${open
            ? 'border-gray-900 bg-white text-gray-900 font-semibold'
            : count > 0
              ? 'border-fom-500 bg-fom-50 text-fom-700 font-semibold'
              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
          }`}
      >
        {display}
        {open
          ? <ChevronUp size={14} className="text-fom-500" />
          : <ChevronDown size={14} />
        }
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 min-w-[260px] py-2 max-h-[300px] overflow-y-auto">
            {count > 0 && (
              <button
                onClick={() => onChange([])}
                className="w-full text-left px-5 py-2.5 text-sm text-red-500 hover:bg-red-50 font-semibold border-b border-gray-100"
              >
                Auswahl aufheben ({count})
              </button>
            )}
            {options.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-3 w-full px-5 py-2.5 text-sm hover:bg-gray-50 border-t border-gray-100 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(opt)}
                  onChange={() => toggle(opt)}
                  className="w-4 h-4 rounded border-gray-300 text-fom-500 focus:ring-fom-500 cursor-pointer"
                />
                <span className={value.includes(opt) ? 'text-fom-600 font-semibold' : 'text-gray-700'}>{opt}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

type SortOption = 'name-az' | 'name-za' | 'start-neu' | 'start-alt'

const SORT_LABELS: Record<SortOption, string> = {
  'name-az': 'Name A\u2013Z',
  'name-za': 'Name Z\u2013A',
  'start-neu': 'Studienstart (neueste)',
  'start-alt': 'Studienstart (\u00e4lteste)',
}

function sortTeilnehmer(list: Teilnehmer[], sort: SortOption): Teilnehmer[] {
  const sorted = [...list]
  switch (sort) {
    case 'name-az':
      return sorted.sort((a, b) => (a.Nachname ?? '').localeCompare(b.Nachname ?? '', 'de'))
    case 'name-za':
      return sorted.sort((a, b) => (b.Nachname ?? '').localeCompare(a.Nachname ?? '', 'de'))
    case 'start-neu':
      return sorted.sort((a, b) => {
        const da = parseDate(getStudienstart(a))?.getTime() ?? 0
        const db = parseDate(getStudienstart(b))?.getTime() ?? 0
        return db - da
      })
    case 'start-alt':
      return sorted.sort((a, b) => {
        const da = parseDate(getStudienstart(a))?.getTime() ?? 0
        const db = parseDate(getStudienstart(b))?.getTime() ?? 0
        return da - db
      })
  }
}

interface Props {
  teilnehmer: Teilnehmer[]
  accountId: string
  filter?: TnFilter | null
  onClearFilter?: () => void
}

type ModalState =
  | { type: 'pending'; tn: Teilnehmer }
  | { type: 'locked'; tn: Teilnehmer; loading: boolean; sent: boolean }
  | null

export default function FirmaTeilnehmer({ teilnehmer, accountId, filter, onClearFilter }: Props) {
  const { firmaToken } = useParams<{ firmaToken: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [modal, setModal] = useState<ModalState>(null)
  const [search, setSearch] = useState('')
  const [studiengangFilter, setStudiengangFilter] = useState<string[]>([])
  const [studienortFilter, setStudienortFilter] = useState<string[]>([])
  const [studienstartFilter, setStudienstartFilter] = useState<string[]>([])

  const [showEhemalige, setShowEhemalige] = useState(false)
  const [sortBy, setSortBy] = useState<SortOption>('name-az')
  const [sortOpen, setSortOpen] = useState(false)
  const [showCount, setShowCount] = useState(9)

  // Aktive oder alle (inkl. Ehemalige)
  const basis = showEhemalige ? teilnehmer : teilnehmer.filter((tn) => !isExmatrikuliert(tn))

  // Basis nach externem Filter (Dashboard-Kacheln)
  const afterExternal = applyFilter(basis, filter ?? null)

  // Kaskadierende Filter: jede Dropdown-Option basiert auf den ANDEREN aktiven Filtern
  const filteredForSg = afterExternal
    .filter(t => studienortFilter.length === 0 || studienortFilter.includes(t.Studienort ?? ''))
    .filter(t => studienstartFilter.length === 0 || studienstartFilter.includes(toSemesterLabel(getStudienstart(t))))
  const filteredForOrt = afterExternal
    .filter(t => studiengangFilter.length === 0 || studiengangFilter.includes(fullStudiengang(t)))
    .filter(t => studienstartFilter.length === 0 || studienstartFilter.includes(toSemesterLabel(getStudienstart(t))))
  const filteredForStart = afterExternal
    .filter(t => studiengangFilter.length === 0 || studiengangFilter.includes(fullStudiengang(t)))
    .filter(t => studienortFilter.length === 0 || studienortFilter.includes(t.Studienort ?? ''))

  const studiengaenge = [...new Set(filteredForSg.map(fullStudiengang).filter(Boolean))].sort()
  const studienorte = [...new Set(filteredForOrt.map(t => t.Studienort).filter(Boolean) as string[])].sort()
  const studienstarts = [...new Set(filteredForStart.map(t => toSemesterLabel(getStudienstart(t))).filter(Boolean))]
    .sort((a, b) => semesterSortKey(b) - semesterSortKey(a))

  const hasAnyFilter = !!(studiengangFilter.length || studienortFilter.length || studienstartFilter.length || filter)

  // Vollständige Filter-Pipeline
  let filtered = afterExternal
  if (studiengangFilter.length > 0) {
    filtered = filtered.filter(t => studiengangFilter.includes(fullStudiengang(t)))
  }
  if (studienortFilter.length > 0) {
    filtered = filtered.filter(t => studienortFilter.includes(t.Studienort ?? ''))
  }
  if (studienstartFilter.length > 0) {
    filtered = filtered.filter(t => studienstartFilter.includes(toSemesterLabel(getStudienstart(t))))
  }

  // Wenn gewählte Filterwerte durch Kaskade weggefallen sind, bereinigen
  const validSg = studiengangFilter.filter(v => studiengaenge.includes(v))
  if (validSg.length !== studiengangFilter.length) setStudiengangFilter(validSg)
  const validOrt = studienortFilter.filter(v => studienorte.includes(v))
  if (validOrt.length !== studienortFilter.length) setStudienortFilter(validOrt)
  const validStart = studienstartFilter.filter(v => studienstarts.includes(v))
  if (validStart.length !== studienstartFilter.length) setStudienstartFilter(validStart)
  if (search.trim()) {
    const q = search.toLowerCase()
    filtered = filtered.filter((tn) =>
      tn.Nachname?.toLowerCase().includes(q) ||
      tn.Vorname?.toLowerCase().includes(q) ||
      tn.Matrikelnr?.toLowerCase().includes(q) ||
      tn.Studiengang?.toLowerCase().includes(q) ||
      tn.Studienfach?.toLowerCase().includes(q) ||
      tn.Studienort?.toLowerCase().includes(q)
    )
  }

  // Sortierung
  const sorted = sortTeilnehmer(filtered, sortBy)
  const visible = sorted.slice(0, showCount)

  const clearAllFilters = () => {
    setStudiengangFilter([])
    setStudienortFilter([])
    setStudienstartFilter([])
    onClearFilter?.()
  }

  async function handleFreigabeAnfordern(tn: Teilnehmer) {
    setModal({ type: 'locked', tn, loading: true, sent: false })
    try {
      await api.freigabeAnfordern(accountId, tn.PersonenFID)
      setModal({ type: 'locked', tn, loading: false, sent: true })
      queryClient.invalidateQueries({ queryKey: ['firma', accountId] })
    } catch {
      setModal({ type: 'locked', tn, loading: false, sent: false })
    }
  }

  function handleCardClick(tn: Teilnehmer) {
    const status = tn.freigabe_status ?? 'locked'
    if (status === 'approved') {
      navigate(`/firma/${firmaToken}/teilnehmer/${tn.PersonenFID}`)
    } else if (status === 'pending') {
      setModal({ type: 'pending', tn })
    }
    // status === 'locked' → keine Aktion; nur der explizite
    // „Freigabe anfordern"-Button öffnet den Bestätigungs-Dialog.
  }

  return (
    <div className="-mx-6 -mt-2">

      {/* ══ Hero-Banner (groß wie Startseite) ══ */}
      <div className="relative h-[600px] overflow-hidden">
        <img src={assetUrl('/login-bg.jpg')} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/20" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
          <h1 className="text-white text-5xl md:text-[4.5rem] font-display font-black tracking-tight drop-shadow-lg">
            Ihre Studierenden
          </h1>
          <p className="text-white/80 text-lg md:text-xl mt-4 max-w-[600px]">
            Hier erhalten Sie alle Informationen über die Leistungen Ihrer Studierenden.
          </p>
        </div>
      </div>

      <div className="px-16 lg:px-28 py-8">

        {/* ══ Filter-Bar ══ */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Filter:</span>
          <FilterButton
            label="Studiengang"
            options={studiengaenge}
            value={studiengangFilter}
            onChange={setStudiengangFilter}
          />
          <FilterButton
            label="Studienstart"
            options={studienstarts}
            value={studienstartFilter}
            onChange={setStudienstartFilter}
          />
          <FilterButton
            label="Studienort"
            options={studienorte}
            value={studienortFilter}
            onChange={setStudienortFilter}
          />

          {/* Ehemalige anzeigen */}
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showEhemalige}
              onChange={(e) => setShowEhemalige(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-fom-500 focus:ring-fom-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">Ehemalige anzeigen</span>
          </label>

          {/* Sortieren */}
          <div className="relative ml-auto">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm border-2 transition-all
                ${sortOpen
                  ? 'border-gray-900 bg-white text-gray-900 font-semibold'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
            >
              <ArrowUpDown size={14} />
              Sortieren nach
              {sortOpen
                ? <ChevronUp size={14} className="text-fom-500" />
                : <ChevronDown size={14} />
              }
            </button>
            {sortOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setSortOpen(false)} />
                <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 min-w-[240px] py-2">
                  {(Object.entries(SORT_LABELS) as [SortOption, string][]).map(([key, label], idx) => (
                    <button
                      key={key}
                      onClick={() => { setSortBy(key); setSortOpen(false) }}
                      className={`w-full text-left px-5 py-3 text-sm hover:bg-gray-50
                        ${idx > 0 ? 'border-t border-gray-100' : ''}
                        ${sortBy === key ? 'text-fom-600 font-semibold' : 'text-gray-700'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Filter entfernen */}
          {hasAnyFilter && (
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center gap-1 px-3 py-2.5 rounded-full text-sm border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
            >
              <X size={14} />
              Filter entfernen
            </button>
          )}
        </div>

        {/* ══ Suchfeld ══ */}
        <div className="relative mb-6">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Studierendensuche"
            className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-300 text-sm
                       bg-white focus:ring-2 focus:ring-fom-500 focus:border-fom-500 outline-none
                       transition-all placeholder:text-gray-400"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* ══ Count ══ */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-900">
            {filtered.length} Studierende{hasAnyFilter ? ' (gefiltert)' : ':'}
          </h2>
        </div>

        {/* ══ Studierenden-Grid ══ */}
        {filtered.length === 0 ? (
          <EmptyState message={search.trim() ? `Keine Treffer f\u00FCr \u201E${search}\u201C` : 'Keine Studierenden gefunden'} icon />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {visible.map((tn, i) => {
                const studienstart = getStudienstart(tn)
                const futureStart = isFutureStart(studienstart)
                const freigabe = tn.freigabe_status ?? 'locked'
                return (
                  <div
                    key={tn.StudiengangTnId ?? i}
                    role={freigabe === 'locked' ? undefined : 'button'}
                    tabIndex={freigabe === 'locked' ? -1 : 0}
                    onClick={freigabe === 'locked' ? undefined : () => handleCardClick(tn)}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && freigabe !== 'locked') handleCardClick(tn) }}
                    className={`group block bg-white border rounded-xl p-5
                               transition-all duration-200 relative
                               ${freigabe === 'locked' ? '' : 'cursor-pointer hover:shadow-lg'}
                               ${futureStart ? 'border-amber-300' : 'border-gray-200'}
                               ${freigabe !== 'locked' ? (futureStart ? 'hover:border-amber-400' : 'hover:border-fom-300') : ''}`}
                  >
                    {/* Zukünftig-Badge (nur wenn freigabe = approved) */}
                    {futureStart && freigabe === 'approved' && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
                        <Clock size={12} />
                        Zukünftig
                      </div>
                    )}

                    {/* Freigabe-Indikator */}
                    {freigabe === 'pending' && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
                        <Clock size={12} />
                        Ausstehend
                      </div>
                    )}
                    {freigabe === 'locked' && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-500 text-xs font-semibold">
                        <Lock size={12} />
                        Gesperrt
                      </div>
                    )}

                    {/* Header: Avatar + Name */}
                    <div className="flex items-center gap-3.5 mb-4">
                      <TnAvatar tn={tn} accountId={accountId} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-gray-900 text-[0.95rem] truncate">
                          {tn.Vorname} {tn.Nachname}
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <GraduationCap size={14} className="text-gray-400 shrink-0" />
                        <span className="truncate">{fullStudiengang(tn) || tn.Studiengang || '–'}</span>
                      </div>
                      {tn.Studienort && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin size={14} className="text-gray-400 shrink-0" />
                          <span>{tn.Studienort}</span>
                        </div>
                      )}
                      {studienstart && (
                        <div className={`flex items-center gap-2 text-sm ${futureStart ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                          <Calendar size={14} className={`shrink-0 ${futureStart ? 'text-amber-500' : 'text-gray-400'}`} />
                          <span>Studienstart {formatDate(studienstart)}</span>
                        </div>
                      )}
                    </div>

                    {/* Aktion: Pfeil (approved) oder Freigabe-Button (locked) */}
                    {freigabe === 'locked' ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setModal({ type: 'locked', tn, loading: false, sent: false }) }}
                        className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                                   bg-fom-500 text-white text-xs font-semibold hover:bg-fom-600 transition-colors
                                   border-none cursor-pointer shadow-sm"
                        title="Freigabe beim Studierenden anfordern"
                      >
                        <Unlock size={12} />
                        Freigabe anfordern
                      </button>
                    ) : (
                      <div className="absolute bottom-4 right-4">
                        <div className="w-8 h-8 rounded-full bg-fom-500 flex items-center justify-center
                                        group-hover:bg-fom-600 transition-colors">
                          <ChevronRight size={16} className="text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Mehr anzeigen */}
            {showCount < filtered.length && (
              <div className="text-center mt-8">
                <button
                  onClick={() => setShowCount((c) => c + 9)}
                  className="px-8 py-2.5 rounded-full border-2 border-fom-500 text-fom-600 text-sm font-semibold
                             hover:bg-fom-50 transition-colors"
                >
                  Mehr anzeigen
                </button>
              </div>
            )}
          </>
        )}

      {/* ══ Freigabe-Modals ══ */}
      {modal !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setModal(null) }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
              <span className="text-sm font-semibold text-gray-700">
                {modal.type === 'pending' ? 'Freigabe ausstehend' : 'Zugriff anfragen'}
              </span>
              <button
                onClick={() => setModal(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors border-none cursor-pointer bg-transparent"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5">
              {modal.type === 'pending' && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock size={16} className="text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {modal.tn.Vorname} {modal.tn.Nachname}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        Die Freigabe für diesen Studierenden wurde bereits angefragt und wartet auf Bestätigung durch den Studierenden.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => setModal(null)}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors border-none cursor-pointer"
                    >
                      Schließen
                    </button>
                  </div>
                </div>
              )}

              {modal.type === 'locked' && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                      <Lock size={16} className="text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {modal.tn.Vorname} {modal.tn.Nachname}
                      </p>
                      {modal.sent ? (
                        <p className="text-sm text-green-700 mt-1 font-medium">
                          Anfrage gesendet! Der Studierende erhält eine E-Mail zur Bestätigung.
                        </p>
                      ) : (
                        <p className="text-sm text-gray-600 mt-1">
                          Sie haben noch keine Freigabe für diesen Studierenden. Anfrage senden, damit der Studierende den Zugriff per E-Mail bestätigen kann.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setModal(null)}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors border-none cursor-pointer"
                    >
                      {modal.sent ? 'Schließen' : 'Abbrechen'}
                    </button>
                    {!modal.sent && (
                      <button
                        onClick={() => handleFreigabeAnfordern(modal.tn)}
                        disabled={modal.loading}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-fom-500 text-white hover:bg-fom-600 transition-colors border-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {modal.loading ? 'Wird gesendet…' : 'Freigabe anfragen'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}